import React, { useEffect, useMemo, useState } from "react";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import Preloader, * as PreloaderStyles from "../../../../../../../common-deprecated/styles/v2/lexus/custom/Preloader";
import {
    IconWrapper,
    ToggleIconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { BnbLabel, useBnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import Collapsable from "../../common/collapsable/Collapsable";
import * as FinanceStyles from "./styles/Finance";
import type { FinanceInsuranceCalculatorType } from "../../../../utils/FinanceInsuranceCalculatorLink";
import { FINANCE_CALCULATOR } from "../../../../../../../common-deprecated/constants";
import FinanceInsuranceCalculator from "../../../common/FinanceInsuranceCalculator";
import { useBnbSelector } from "../../../../redux/store";
import useFinanceToggle from "../../../../hooks/shared/actions/useFinanceToggle";
import { setFinanceCollapsable } from "../../../../redux/actions/FinanceInsuranceActions";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { getBnbMonthlyRateFormat } from "../../../../utils/settings/bnbFinanceSettingUtils";
import MonthlyPrice from "../../common/price/MonthlyPrice";
import { showFinanceInsurance } from "../../../../utils/financeInsuranceUtils";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/ScreenreaderOnly";
import { trackClickedLink, trackInPageCalculator } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type PropsType = {
    calculatorType?: FinanceInsuranceCalculatorType;
    expandedByDefault?: boolean; // When provided the finance section is expanded by default
    showIntroduction?: boolean;
};
const Finance = ({
    calculatorType = FINANCE_CALCULATOR,
    expandedByDefault = false,
    showIntroduction = true,
}: PropsType): JSX.Element | null => {
    const finance = useBnbSelector((state) => state.finance);
    const { total, totalWithoutPromotions, collapsableActive, info } = finance;
    const { bnbSettings, car, commonSettings } = useBnbSelector((state) => state);
    const step = useBnbSelector((state) => state.navigation.step);
    const basketTotal = useBnbSelector((state) => state.basket.total);
    const modelCode = useBnbSelector((state) => state.car.model.internalCode || "");
    const [financeDetailsExpanded, setFinanceDetailsExpanded] = useState<boolean>(false);
    const { showMonthlyLoader, financeOption } = useFinanceToggle();

    const monthlyFormat = getBnbMonthlyRateFormat(commonSettings, modelCode);
    const dispatch = useDispatch();

    const [financeOptionLabel, financeOptionLabelMonthly, financeCollapsableCtaLabel] = useBnbLabel([
        "financeButton",
        "financeCollapsableTitle",
        "financeCollapsableCta",
    ]);
    const financeButtonEditLabel = useBnbOptionalLabel("financeButtonEdit");

    // Financial Information
    const totalPrice = useMemo(
        () => ({
            ...basketTotal,
            monthly: Number(total),
            monthlyWithoutPromotion: Number(totalWithoutPromotions),
        }),
        [basketTotal, total, totalWithoutPromotions],
    );

    const showTotalPrice =
        totalPrice &&
        totalPrice.monthly !== 0 &&
        (!bnbSettings.enableFinanceCompare || (bnbSettings.enableFinanceCompare && finance.quotes.finance));

    useEffect(() => {
        if (expandedByDefault && !collapsableActive) {
            dispatch(setFinanceCollapsable(true));
        }
    }, []);

    if (
        !showFinanceInsurance({
            type: "finance",
            bnbSettings,
            car,
            commonSettings,
            finance,
            step,
        })
    ) {
        return null;
    }

    // TRIGGERS AFTER THE COLLAPSABLE HAS EXPANDED/CLOSED
    const onChange = (expanded: boolean): void => {
        dispatch(setFinanceCollapsable(expanded));
    };

    // Only Triggers after the collapsable button is clicked to open/close the collapsable
    const onTrack = (): void => {
        const trackingLabel = !collapsableActive ? "open-inpage-finance-calculator" : "close-inpage-finance-calculator";
        trackInPageCalculator(!collapsableActive ? "cc-cta" : "cc-action", trackingLabel, step);
    };

    return (
        <FinanceStyles.Wrapper id="finance-injection-container">
            {showIntroduction && (
                <>
                    <FinanceStyles.Title>
                        <BnbLabel label="financeInPageTitle" />
                    </FinanceStyles.Title>
                    <FinanceStyles.Description>
                        <BnbLabel label="financeInPageText" useHTML />
                    </FinanceStyles.Description>
                    <FinanceStyles.BenefitsContainer
                        onToggle={() => {
                            setFinanceDetailsExpanded(!financeDetailsExpanded);
                            if (!financeDetailsExpanded) {
                                trackClickedLink("finance-benefits");
                            }
                        }}
                    >
                        <FinanceStyles.BenefitsHeader>
                            <BnbLabel label="financeInPageDetailsLabel" />
                            <ToggleIconWrapper isDown={financeDetailsExpanded}>
                                <Icon variant="chevronDown" />
                            </ToggleIconWrapper>
                        </FinanceStyles.BenefitsHeader>
                        <FinanceStyles.BenefitsContent>
                            <BnbLabel label="financeInPageDetails" useHTML />
                        </FinanceStyles.BenefitsContent>
                    </FinanceStyles.BenefitsContainer>
                </>
            )}
            <Collapsable
                headerTitle={financeOption === "monthly" ? financeOptionLabelMonthly : financeOptionLabel}
                headerDescription={
                    showTotalPrice && (
                        <Flex.Row>
                            <Flex.Col pr={0}>
                                <MonthlyPrice
                                    price={totalPrice}
                                    priceFormat={monthlyFormat}
                                    showLoader={showMonthlyLoader}
                                    forceHideDisclaimer
                                    financeInfo={info}
                                    size="small"
                                />
                            </Flex.Col>
                            {info && info.disclaimer && (
                                <Flex.Col p={0}>
                                    <Button.Link
                                        type="button"
                                        hasIconRight
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (info?.disclaimer) {
                                                dispatch(
                                                    showModal({
                                                        type: MODAL_FINANCE_DISCLAIMER,
                                                        disclaimer: info.disclaimer,
                                                        productName: "",
                                                    }),
                                                );
                                            }
                                        }}
                                    >
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                        <SrOnly>
                                            <BnbLabel label="bnbA11yButtonMoreInfo" />
                                        </SrOnly>
                                    </Button.Link>
                                </Flex.Col>
                            )}
                        </Flex.Row>
                    )
                }
                ctaLabel={showTotalPrice ? financeButtonEditLabel : financeCollapsableCtaLabel}
                isPersonalized={!!showTotalPrice}
                icon={<Icon variant="card" size="medium" />}
                content={
                    <FinanceInsuranceCalculator
                        type={calculatorType}
                        visible={collapsableActive}
                        showLoader={showMonthlyLoader}
                        loader={
                            <PreloaderStyles.PreLoaderWrapper hasBorder hasButtonHeight>
                                <Preloader size={30} />
                            </PreloaderStyles.PreLoaderWrapper>
                        }
                    />
                }
                active={collapsableActive}
                onChange={onChange}
                onTrack={onTrack}
            />
        </FinanceStyles.Wrapper>
    );
};

export default Finance;
